import { Box } from "@chakra-ui/react"
import axios from "axios"
import dynamic from "next/dynamic"
import { useRouter } from "next/router"
import { useEffect, useRef, useState } from "react"
import { useRecoilValue, useSetRecoilState } from "recoil"
import { FH_URL_PARAMS, brands, fhAnalyticsUserActions, urlPatterns } from "../../../../common/constants/constants"
import {
  itemsPerPage,
  queries,
  ranges,
  searchDefaults,
  searchResultStatus,
  tabs,
} from "../../../../common/constants/search-constants"
import { useQueryParam } from "../../../../common/lib/QueryParamContext"
import { UrlParamsContext } from "../../../../common/lib/UrlParamsContext"
import sendFhAnalytics from "../../../../common/lib/fredhopper"
import { onboardingIsRunningState, onboardingShouldRunState } from "../../../../common/store/onboardingStore"
import { countryIdState, searchServiceLocaleState } from "../../../../common/store/settingsStore"
import { Tab } from "../../../../common/types/global"
import { getNewPathWithParam } from "../../../../common/utils/router-utils"
import { createTransformedMeta } from "../../../../common/utils/search-create-transformed-meta"
import {
  getFhLocationParam,
  getFhStartIndexParam,
  getFhSuppressParam,
  getPageParam,
  getSearchQueryUri,
  getSeriesDisplayRankingParam,
  getSystemDisplayRankingParam,
} from "../../../../common/utils/search-query"
import {
  getFacetmap,
  getInitialSelectedTab,
  getItems,
  getTransformedImplicitRanges,
  getTrimmedUrlParams,
} from "../../../../common/utils/search-utils"
import { pushHistoryState } from "../../article-table/helpers/modify-query-params"
import SearchResultsView from "../SearchResultsView/SearchResultsView"

const Loader = dynamic(() => import("../../../shared/Loader"), { ssr: false, loading: () => <Box mt="12" /> })

const SearchResultsContainer = ({ initialTransformedMeta, initialSearchResults, host }) => {
  const router = useRouter()
  const { query, asPath, locale } = router
  const { queryParam, setQueryParam } = useQueryParam()

  const defaultSearchResult = {
    items: [],
    implicitRanges: null,
    allSearchFilters: null,
    rid: null, //fh response id for analytics
    selectedColors: null,
  }

  //Determine the initial search page state based on urlParams query parameter present in the url
  const initialUrlParams = (query[FH_URL_PARAMS] as string) ?? null

  //Determine the initial selected tab by the passed url, default is 'PRO'
  const initialSelectedTab = getInitialSelectedTab(initialUrlParams, asPath)

  const [urlParams, setUrlParams] = useState(initialUrlParams)
  const [isLoading, setIsLoading] = useState(false)
  const [transformedMeta, setTransformedMeta] = useState(initialTransformedMeta)
  const [searchResult, setSearchResult] = useState(initialSearchResults ?? defaultSearchResult)
  const [selectedTab, setSelectedTab] = useState<Tab>(initialSelectedTab)
  const [pageNumber, setPageNumber] = useState(searchDefaults.START_PAGE)
  const brand = query?.brand as string
  const searchServiceLocale = useRecoilValue(searchServiceLocaleState)
  const countryId = useRecoilValue(countryIdState)

  // Use this ref to detect first render and run query effects only after the first one for SSR purposes
  const isFirstRender = useRef(true)

  useEffect(() => {
    const firstRenderTimeout = setTimeout(() => (isFirstRender.current = false), 100)
    return () => {
      clearTimeout(firstRenderTimeout)
    }
  }, [])

  //When the selected tab is changed in the URL update the local state
  useEffect(() => {
    setSelectedTab(initialSelectedTab)
  }, [initialSelectedTab])

  /* Effect to update the selected tab in the url */
  useEffect(() => {
    /* Update the product type in the url */
    let newPath = null

    if (selectedTab === tabs.PRO && asPath.includes(urlPatterns.SPARE_PARTS_DIRECTORY)) {
      newPath = asPath.replace(urlPatterns.SPARE_PARTS_DIRECTORY, urlPatterns.PRODUCTS_DIRECTORY)
    }
    if (selectedTab === tabs.SPT && asPath.includes(urlPatterns.PRODUCTS_DIRECTORY)) {
      newPath = asPath.replace(urlPatterns.PRODUCTS_DIRECTORY, urlPatterns.SPARE_PARTS_DIRECTORY)
    }

    if (newPath) {
      router.replace(
        {
          query,
        },
        newPath,
        { shallow: true }
      )
    }
    /* END Update the product type in url */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTab])

  //If we have a query param, a search is performed
  //This check is needed for the lazy loading,
  //we are pre-loading the component before the search is performed by the user
  const isSearch = !!queryParam

  /* Reset urlParams local state when there is no urlParams query param
    reset meta data to show the loader and clear the search results object */
  useEffect(() => {
    if (initialUrlParams) return

    setUrlParams(null)
    setTransformedMeta(initialTransformedMeta)
    setSearchResult(initialSearchResults ?? defaultSearchResult)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialUrlParams, queryParam?.value?.toString()])

  //Get the meta query effect
  useEffect(() => {
    if (
      !isSearch ||
      !searchServiceLocale ||
      !countryId ||
      !brand ||
      (!!initialTransformedMeta && isFirstRender.current)
    )
      return

    setIsLoading(true)

    const fhLocation = getFhLocationParam(
      queryParam,
      searchServiceLocale,
      countryId,
      selectedTab,
      brands[brand],
      queries.META,
      urlParams
    )
    const fhSuppress = getFhSuppressParam(queries.META)
    const page = getPageParam(queryParam, urlParams)
    const metaQueryUri = getSearchQueryUri({ fhLocation, fhSuppress, page })

    axios
      .get(metaQueryUri)
      .then((res) => {
        console.log("META RESPONSE: ", res)
        handleMetaResponse(res)
      })
      .catch((err) => {
        console.log("META ERROR: ", err)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlParams, queryParam?.value?.toString(), selectedTab, brand, searchServiceLocale])

  //Get the search results query effect
  useEffect(() => {
    if (
      !transformedMeta ||
      (transformedMeta.showNoResultsView && !transformedMeta.showNoResultsView) ||
      transformedMeta.searchPass === searchResultStatus.HAS_PARTIALS ||
      (!!initialSearchResults && isFirstRender.current)
    )
      return

    setIsLoading(true)

    const fhLocation = getFhLocationParam(
      queryParam,
      searchServiceLocale,
      countryId,
      selectedTab,
      brands[brand],
      queries.SEARCH,
      urlParams
    )
    const fhSuppress = getFhSuppressParam(queries.SEARCH, true)
    const page = getPageParam(queryParam, urlParams)
    const systemDisplayRanking = getSystemDisplayRankingParam(queryParam, selectedTab, queries.SEARCH, countryId)
    const seriesDisplayRanking = getSeriesDisplayRankingParam(queryParam, selectedTab, queries.SEARCH, countryId)
    const fhViewSize = itemsPerPage[selectedTab]
    const fhStartIndex = getFhStartIndexParam(pageNumber, selectedTab)
    const searchQueryUri = getSearchQueryUri({
      fhLocation,
      fhSuppress,
      page,
      systemDisplayRanking,
      seriesDisplayRanking,
      fhViewSize,
      fhStartIndex,
    })

    axios
      .get(searchQueryUri)
      .then((res) => {
        console.log("SEARCH RESPONSE: ", res)
        handleSearchResultResponse(res)
        setIsLoading(false)
        //track pageview analytics event on every FH request for products & spt
        sendFhAnalytics({ locale, query, action: fhAnalyticsUserActions.PAGEVIEW, sourceId: res?.data?.rid })
      })
      .catch((err) => {
        console.log("SEARCH ERROR: ", err)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transformedMeta, pageNumber])

  const handleMetaResponse = (res) => {
    const transformedMeta = createTransformedMeta(res)

    //If urlParams is present but the state of the search page can't be restored
    //remove the param from the url and send a new request to FH from the data in queryParam
    if (queryParam.urlParams && transformedMeta.showNoResultsView && !transformedMeta.showFiltersNoResultsView) {
      //Delete urlParams from the url and push the new url to history object
      pushHistoryState([{ action: "delete", data: [FH_URL_PARAMS] }])

      //Copy the queryParam object but set the urlParams property to null
      //so that useState receives a new object reference to trigger re-render
      setQueryParam({ ...queryParam, urlParams: null })
      return
    }

    handleSetSelectedTab(handleTabSelection(transformedMeta.typeSelector, selectedTab))
    setTransformedMeta(transformedMeta)
  }

  const handleSearchResultResponse = (res) => {
    const resultItems = getItems(res)

    /* If a subsequent page is requested, append the results for the new page to the already displayed results */
    const renderItems = pageNumber === searchDefaults.START_PAGE ? resultItems : [...searchResult.items, ...resultItems]

    //Implicit ranges are needed to determine if a result tile should be a product or an article
    const rawImplicitRanges = res.data?.info?.[ranges.RANGES]?.[ranges.IMPLICIT_RANGES]?.[ranges.RANGE]

    const rawQueryRanges = res.data?.info?.[ranges.RANGES]?.[ranges.QUERY_RANGES]?.[ranges.RANGE]
    const colorQueryRange = rawQueryRanges?.find((range) => range.includes("/colour>")) ?? ""

    const searchFacetmap = getFacetmap(res)

    setSearchResult({
      items: renderItems,
      implicitRanges: getTransformedImplicitRanges(rawImplicitRanges),
      allSearchFilters: searchFacetmap.filter,
      rid: res.data?.rid,
      selectedColors: colorQueryRange !== "" ? colorQueryRange.split(">{")[1].replace("}", "") : null,
    })
  }

  const handleTabSelection = (typeSelector, selectedTab) => {
    //If the currently selected tab has items in results return it
    //Else return the first tab that has items in results from right to left
    return hasTabResults(typeSelector, selectedTab)
      ? selectedTab
      : Object.keys(tabs).find((tab) => hasTabResults(typeSelector, tab))
  }

  const hasTabResults = (typeSelector, tab) => {
    //If we don't have the typeSelector return true so the last selected tab is used
    return typeSelector?.filtersection?.some((f) => f.value.value === tab) ?? true
  }

  const resetPaginationState = () => {
    setSearchResult(defaultSearchResult)
    setPageNumber(searchDefaults.START_PAGE)
  }

  const handleSetSelectedTab = (tab: Tab, _urlParams: string = null) => {
    if (tab === selectedTab) return

    resetPaginationState()

    // If coming from the SearchTab component
    if (_urlParams) {
      // Update the url_params of the clicked tab in the state
      if (query[FH_URL_PARAMS]) handleSetUrlParams(_urlParams)

      /* Update the product type in the url */
      let newPath = asPath

      if (tab === tabs.PRO) newPath = newPath.replace(urlPatterns.SPARE_PARTS_DIRECTORY, urlPatterns.PRODUCTS_DIRECTORY)
      if (tab === tabs.SPT) newPath = newPath.replace(urlPatterns.PRODUCTS_DIRECTORY, urlPatterns.SPARE_PARTS_DIRECTORY)

      router.push(
        {
          query,
        },
        newPath,
        { shallow: true }
      )
      /* END Update the product type in url */
    }

    setSelectedTab(tab)
  }

  const handleSetUrlParams = (_urlParams: string) => {
    if (_urlParams === urlParams) return

    resetPaginationState()

    const trimmedUrlParams = getTrimmedUrlParams(_urlParams)
    const newPath = getNewPathWithParam(asPath, FH_URL_PARAMS, trimmedUrlParams)

    router.push(
      {
        query: {
          ...query,
          urlParams: trimmedUrlParams, // override the urlParams property
        },
      },
      newPath,
      { shallow: true }
    )

    setUrlParams(trimmedUrlParams)
  }

  const onboardingShouldRun = useRecoilValue(onboardingShouldRunState)
  const setOnboardingIsRunning = useSetRecoilState(onboardingIsRunningState)

  /* When the page loads check to see if we should set the user onboarding recoil state to true
  so the onboarind can start again */
  useEffect(() => {
    if (isSearch && !isLoading && transformedMeta && onboardingShouldRun) {
      setOnboardingIsRunning(true)
    }
  }, [isSearch, transformedMeta, isLoading, onboardingShouldRun, setOnboardingIsRunning])

  return (
    isSearch &&
    (!transformedMeta ? (
      <Loader mt="12" />
    ) : (
      <UrlParamsContext.Provider value={{ urlParams, setUrlParams: handleSetUrlParams }}>
        <SearchResultsView
          host={host}
          transformedMeta={transformedMeta}
          searchResult={searchResult}
          selectedTab={selectedTab}
          handleSetSelectedTab={handleSetSelectedTab}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          isLoading={isLoading}
        />
      </UrlParamsContext.Provider>
    ))
  )
}

export default SearchResultsContainer
